import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { API, useAuth } from "../context/AuthContext";
import { useI18n } from "../context/I18nContext";
import { toast } from "sonner";

export default function BillingSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { t } = useI18n();
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState("pending"); // pending | paid | failed

  useEffect(() => {
    if (!sessionId) {
      setState("failed");
      return;
    }
    let cancelled = false;
    let attempts = 0;
    const poll = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const r = await fetch(`${API}/checkout/status/${sessionId}`, {
          credentials: "include",
        });
        if (!r.ok) throw new Error("status");
        const data = await r.json();
        if (data.payment_status === "paid") {
          setState("paid");
          await refresh();
          toast.success("PRO ACTIVÉ");
          return;
        }
        if (data.status === "expired" || attempts >= 8) {
          setState("failed");
          return;
        }
        setTimeout(poll, 2000);
      } catch (e) {
        if (attempts >= 8) {
          setState("failed");
        } else {
          setTimeout(poll, 2000);
        }
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId, refresh]);

  return (
    <main
      data-testid="billing-success"
      className="flex-grow flex items-center justify-center px-6 py-16"
    >
      <div className="bg-white border-2 border-[#0A0A0A] shadow-brutal p-10 max-w-xl w-full">
        {state === "pending" && (
          <>
            <p className="text-[10px] tracking-[0.3em] uppercase text-[#FF3B30] font-bold">
              // STRIPE
            </p>
            <h1 className="font-display text-3xl uppercase mt-3">
              <span className="blink">▮</span> {t("pay_pending")}
            </h1>
          </>
        )}
        {state === "paid" && (
          <>
            <p className="text-[10px] tracking-[0.3em] uppercase text-[#FFCC00] font-bold bg-[#0A0A0A] inline-block px-2 py-1">
              ✓ {t("pro_badge")}
            </p>
            <h1 className="font-display text-3xl md:text-4xl uppercase mt-4 leading-tight">
              {t("pay_success_title")}
            </h1>
            <p className="font-mono-corp text-base mt-4 text-[#4B4B4B]">
              {t("pay_success_body")}
            </p>
            <button
              data-testid="back-to-app-btn"
              onClick={() => navigate("/app")}
              className="mt-8 bg-[#FF3B30] text-white border-2 border-[#0A0A0A] font-display text-sm uppercase tracking-widest px-6 py-3 shadow-brutal-sm hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-all"
            >
              {t("back_to_app")} →
            </button>
          </>
        )}
        {state === "failed" && (
          <>
            <p className="text-[10px] tracking-[0.3em] uppercase text-[#FF3B30] font-bold">
              // ERROR
            </p>
            <h1 className="font-display text-3xl uppercase mt-3">
              {t("pay_error")}
            </h1>
            <button
              data-testid="back-to-app-btn"
              onClick={() => navigate("/app")}
              className="mt-6 bg-white text-[#0A0A0A] border-2 border-[#0A0A0A] font-display text-sm uppercase tracking-widest px-6 py-3 shadow-brutal-sm hover:bg-[#FFCC00] transition-all"
            >
              {t("back_to_app")}
            </button>
          </>
        )}
      </div>
    </main>
  );
}
